// src/utils/cuadre.utils.js
// ═══════════════════════════════════════════════════════════════════════════
// 🖨️ CUADRE DE CIERRE DE CAJA - eFIT POS
// Genera el recibo de cierre de turno para impresora térmica (80mm / 58mm)
// ═══════════════════════════════════════════════════════════════════════════

const safe = (n) => Number(n || 0) || 0;

const fmt = (n, simbolo = 'RD$') =>
  `${simbolo}${safe(n).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtFecha = (fecha) => {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleString('es-DO', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const linea = (ancho) => '─'.repeat(ancho > 58 ? 32 : 24);

/**
 * Genera el HTML del cuadre de cierre de caja.
 *
 * @param {Object} caja    - Objeto caja cerrada (codigo, usuario_apertura_nombre, fecha_apertura, fecha_cierre, caja_fisica_numero)
 * @param {Object} resumen - { monto_inicial, ventas_efectivo, ventas_tarjeta, ventas_transferencia,
 *                            total_ventas, num_ventas, ingresos_adicionales, egresos,
 *                            monto_esperado, monto_real, diferencia }
 * @param {Object} config  - { empresa_nombre, empresa_rnc, empresa_direccion, empresa_telefono,
 *                            fiscal: { moneda: { simbolo } } }
 * @param {Object} [opts]
 * @param {number} [opts.anchoMm=80]
 */
export const generarHTMLCuadre = (caja, resumen, config = {}, opts = {}) => {
  const { anchoMm = 80 } = opts;
  const ancho = anchoMm === 58 ? '58mm' : '80mm';
  const simbolo = config?.fiscal?.moneda?.simbolo || 'RD$';

  const dif = safe(resumen.diferencia);
  const estadoColor = dif === 0 ? '#10b981' : dif > 0 ? '#3b82f6' : '#ef4444';
  const estadoLabel = dif === 0 ? '✅ CUADRADO' : dif > 0 ? '💰 SOBRANTE' : '⚠️ FALTANTE';

  const row = (label, valor, bold = false) => `
    <tr>
      <td style="padding:2px 0; color:#374151; ${bold ? 'font-weight:700;' : ''}">${label}</td>
      <td style="padding:2px 0; text-align:right; ${bold ? 'font-weight:700;' : ''}">${valor}</td>
    </tr>`;

  const sep = () => `
    <tr><td colspan="2" style="padding:4px 0;">
      <div style="border-top:1px dashed #94a3b8;"></div>
    </td></tr>`;

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Cuadre de Caja</title>
  <style>
    @page { size: ${ancho} auto; margin: 4mm 3mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Courier New', monospace;
      font-size: ${anchoMm === 58 ? '11px' : '12px'};
      width: ${ancho};
      color: #1e293b;
      background: white;
    }
    .center { text-align: center; }
    .bold   { font-weight: 700; }
    .empresa { font-size: ${anchoMm === 58 ? '13px' : '15px'}; font-weight: 800; }
    .titulo {
      font-size: ${anchoMm === 58 ? '14px' : '16px'};
      font-weight: 800;
      background: #1e3a8a;
      color: white;
      padding: 6px 4px;
      margin: 6px 0;
      letter-spacing: 1px;
    }
    table { width: 100%; border-collapse: collapse; }
    .estado-box {
      background: ${estadoColor}22;
      border: 2px solid ${estadoColor};
      border-radius: 6px;
      padding: 8px 4px;
      text-align: center;
      font-size: ${anchoMm === 58 ? '16px' : '18px'};
      font-weight: 800;
      color: ${estadoColor};
      margin: 8px 0;
    }
    .firma-line {
      border-bottom: 1px solid #334155;
      height: 24px;
      margin: 4px 0 2px;
    }
    .section-title {
      font-weight: 700;
      color: #1e3a8a;
      font-size: ${anchoMm === 58 ? '11px' : '12px'};
      margin: 6px 0 2px;
    }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <!-- ENCABEZADO EMPRESA -->
  <div class="center" style="padding:4px 0 2px;">
    <div class="empresa">${config.empresa_nombre || 'eFIT POS'}</div>
    ${config.empresa_rnc ? `<div>RNC: ${config.empresa_rnc}</div>` : ''}
    ${config.empresa_direccion ? `<div style="font-size:10px;">${config.empresa_direccion}</div>` : ''}
    ${config.empresa_telefono ? `<div>Tel: ${config.empresa_telefono}</div>` : ''}
  </div>

  <!-- TÍTULO -->
  <div class="titulo center">CUADRE DE CAJA</div>

  <!-- DATOS DEL TURNO -->
  <div class="section-title">DATOS DEL TURNO</div>
  <table>
    ${row('Caja:', caja.caja_fisica_numero || caja.numero_caja || '—')}
    ${row('Código:', caja.codigo || '—')}
    ${row('Cajero:', caja.usuario_apertura_nombre || '—')}
    ${row('Apertura:', fmtFecha(caja.fecha_apertura))}
    ${row('Cierre:', fmtFecha(caja.fecha_cierre))}
    ${row('# Ventas:', resumen.num_ventas || 0)}
  </table>

  <!-- VENTAS POR MÉTODO -->
  <div class="section-title" style="margin-top:8px;">VENTAS POR MÉTODO DE PAGO</div>
  <table>
    ${row('Total ventas:', fmt(resumen.total_ventas, simbolo), true)}
    ${sep()}
    ${row('Efectivo:', fmt(resumen.ventas_efectivo, simbolo))}
    ${row('Tarjeta:', fmt(resumen.ventas_tarjeta, simbolo))}
    ${row('Transferencia:', fmt(resumen.ventas_transferencia, simbolo))}
    ${safe(resumen.total_ventas) - safe(resumen.ventas_efectivo) - safe(resumen.ventas_tarjeta) - safe(resumen.ventas_transferencia) > 0.01
      ? row('Mixto:', fmt(safe(resumen.total_ventas) - safe(resumen.ventas_efectivo) - safe(resumen.ventas_tarjeta) - safe(resumen.ventas_transferencia), simbolo))
      : ''}
  </table>

  <!-- CUADRE DE EFECTIVO -->
  <div class="section-title" style="margin-top:8px;">CUADRE DE EFECTIVO</div>
  <table>
    ${row('Monto inicial:', fmt(resumen.monto_inicial, simbolo))}
    ${row('+ Ventas efectivo:', fmt(resumen.ventas_efectivo, simbolo))}
    ${safe(resumen.ingresos_adicionales) > 0 ? row('+ Ingresos extra:', fmt(resumen.ingresos_adicionales, simbolo)) : ''}
    ${safe(resumen.egresos) > 0 ? row('- Egresos:', fmt(resumen.egresos, simbolo)) : ''}
    ${sep()}
    ${row('= Esperado:', fmt(resumen.monto_esperado, simbolo), true)}
    ${row('Contado:', fmt(resumen.monto_real, simbolo), true)}
    ${sep()}
    ${row('Diferencia:', `${dif >= 0 ? '+' : ''}${fmt(dif, simbolo)}`, true)}
  </table>

  <!-- ESTADO -->
  <div class="estado-box">${estadoLabel}</div>

  ${dif !== 0 ? `
  <!-- NOTA DIFERENCIA -->
  <div style="font-size:10px; padding:4px; border:1px dashed ${estadoColor}; border-radius:4px; color:${estadoColor}; margin-bottom:6px;">
    ${dif > 0
      ? `Sobrante de ${fmt(dif, simbolo)}. El monto extra debe ser entregado al supervisor.`
      : `Faltante de ${fmt(Math.abs(dif), simbolo)}. Ver observaciones en el sistema.`
    }
  </div>` : ''}

  <!-- FIRMAS -->
  <div style="margin-top:10px;">
    <div style="display:flex; gap:16px;">
      <div style="flex:1;">
        <div class="firma-line"></div>
        <div class="center" style="font-size:10px;">Cajero</div>
        <div class="center" style="font-size:10px;">${caja.usuario_apertura_nombre || ''}</div>
      </div>
      <div style="flex:1;">
        <div class="firma-line"></div>
        <div class="center" style="font-size:10px;">Supervisor</div>
      </div>
    </div>
  </div>

  <!-- PIE -->
  <div class="center" style="margin-top:10px; font-size:10px; color:#64748b;">
    Impreso: ${fmtFecha(new Date())}<br>
    eFIT POS — ${config.empresa_nombre || ''}
  </div>

  <script>
    window.onload = function() {
      setTimeout(function() { window.print(); window.close(); }, 300);
    };
  </script>
</body>
</html>`;
};

/**
 * Abre ventana de impresión con el cuadre de cierre.
 */
export const imprimirCuadreCierre = (caja, resumen, config, opts = {}) => {
  const html = generarHTMLCuadre(caja, resumen, config, opts);
  const ventana = window.open('', '_blank', 'width=400,height=700');
  if (!ventana) {
    console.warn('⚠️ Ventana de impresión bloqueada. Permite pop-ups para este sitio.');
    return;
  }
  ventana.document.write(html);
  ventana.document.close();
};
